import { useCallback, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { api } from '../../servizi/api.ts'
import { useSessione } from '../../servizi/sessione.tsx'
import { Attesa, Avviso, Vuoto } from '../../componenti/Pezzi.tsx'
import {
  AreaTesto, Bottone, Etichetta, Modale, Paginazione, Riquadro, Scelta, Tabella, type TonoEtichetta,
} from '../../componenti/base.tsx'
import { LIMITE, giorno, messaggioErrore, usaElenco } from '../../componenti/elenco.tsx'

/**
 * Segnalazioni della migrazione (legacy.anomalia), una per una (461).
 *
 * In testa i tipi di segnalazione presenti, con che cosa significano e quante restano da
 * sistemare; sotto l'elenco filtrato, con il collegamento al dato e i comandi «Risolta» e «Da
 * ignorare». I filtri stanno nell'indirizzo. Quando una segnalazione riguarda la scheda di una
 * persona, nome e dettaglio arrivano dall'API solo a chi legge quella scheda.
 */

export type Regola = {
  regola: string; titolo: string | null; spiegazione: string | null; cosa_fare: string | null
  chiusura_automatica: string | null
  aperte: number; in_lavorazione: number; risolte: number; ignorate: number; gravita: string
}

type Persona = { id: string; etichetta: string; numero_scheda: number | null; in_chiaro: boolean }

type Anomalia = {
  id: string; regola: string; titolo: string | null; gravita: string; stato: string
  descrizione: string; suggerimento: string | null; riservata: boolean
  persone: Persona[]; persone_nascoste: number
  oggetto: { tipo: string; id: string; persona_id: string | null } | null
  creata_il: string; risolta_il: string | null; risolta_da: string | null; nota: string | null
}

type EsitoRicontrollo = {
  chiuse: number; riaperte: number
  per_regola: { regola: string; titolo: string | null; chiuse: number; riaperte: number }[]
}

export const titoloRegola = (titolo: string | null | undefined) => titolo ?? 'Altra segnalazione'

const GRAVITA: Record<string, { nome: string; tono: TonoEtichetta }> = {
  bloccante: { nome: 'bloccante', tono: 'critico' },
  attenzione: { nome: 'da verificare', tono: 'attenzione' },
  info: { nome: 'informativa', tono: 'neutro' },
}

const STATO: Record<string, { nome: string; tono: TonoEtichetta }> = {
  aperta: { nome: 'da sistemare', tono: 'attenzione' },
  in_lavorazione: { nome: 'in lavorazione', tono: 'informativo' },
  risolta: { nome: 'risolta', tono: 'positivo' },
  ignorata: { nome: 'ignorata', tono: 'neutro' },
}

const FILTRO_STATO = [
  { valore: 'da_sistemare', etichetta: 'Da sistemare' },
  { valore: 'aperta', etichetta: 'Non ancora prese in carico' },
  { valore: 'in_lavorazione', etichetta: 'In lavorazione' },
  { valore: 'risolta', etichetta: 'Risolte' },
  { valore: 'ignorata', etichetta: 'Ignorate' },
  { valore: 'tutte', etichetta: 'Tutte' },
]

/** Dove porta il collegamento di una segnalazione, e chi puo' seguirlo. */
function collegamento(o: NonNullable<Anomalia['oggetto']>, puo: (p: string) => boolean)
  : { a: string; testo: string } | null {
  switch (o.tipo) {
    case 'persona': return { a: `/persone/${o.id}`, testo: 'Apri la scheda' }
    case 'intervento':
      return o.persona_id && puo('intervento.modifica')
        ? { a: `/persone/${o.persona_id}/intervento/${o.id}`, testo: 'Apri l’intervento' }
        : o.persona_id ? { a: `/persone/${o.persona_id}`, testo: 'Apri la scheda' } : null
    case 'ente': return puo('ente.leggi') ? { a: `/enti/${o.id}`, testo: 'Apri l’ente' } : null
    case 'sede': return puo('sede.gestisci') ? { a: `/sedi/${o.id}`, testo: 'Apri la sede' } : null
    case 'servizio': return puo('servizio_erogato.leggi') ? { a: `/registro/${o.id}`, testo: 'Apri il servizio' } : null
    case 'progetto': return puo('progetto.leggi') ? { a: `/progetti/${o.id}`, testo: 'Apri il progetto' } : null
    case 'utente': return puo('utente.leggi') ? { a: '/amministrazione', testo: 'Apri Utenti' } : null
    case 'decisione': return { a: '/migrazione?stato_decisione=decise', testo: 'Vedi le decisioni prese' }
    default: return null
  }
}

export function Segnalazioni({ regole, ricaricaStato }: { regole: Regola[]; ricaricaStato: () => void }) {
  const { puo } = useSessione()
  const puoDecidere = puo('migrazione.decidi')
  const [parametri, setParametri] = useSearchParams()
  const regola = parametri.get('regola') ?? ''
  const gravita = parametri.get('gravita') ?? ''
  const statoGrezzo = parametri.get('stato') ?? ''
  const stato = FILTRO_STATO.some((s) => s.valore === statoGrezzo) ? statoGrezzo : 'da_sistemare'

  const aggiorna = useCallback((valori: Record<string, string>) => {
    setParametri((attuali) => {
      const nuovi = new URLSearchParams(attuali)
      for (const [chiave, valore] of Object.entries(valori)) {
        if (valore) nuovi.set(chiave, valore); else nuovi.delete(chiave)
      }
      return nuovi
    }, { replace: true })
  }, [setParametri])

  const percorso = useMemo(() => {
    const q = new URLSearchParams({ stato })
    if (regola) q.set('regola', regola)
    if (gravita) q.set('gravita', gravita)
    return `/migrazione/anomalie?${q.toString()}`
  }, [stato, regola, gravita])
  const { dati, errore, finestra, ricarica } = usaElenco<Anomalia>(percorso)

  const [ricontrollo, setRicontrollo] = useState<{ inCorso: boolean; esito: EsitoRicontrollo | null }>(
    { inCorso: false, esito: null })
  const [erroreComando, setErroreComando] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState<string | null>(null)
  const [daSegnare, setDaSegnare] = useState<{ anomalia: Anomalia; stato: 'risolta' | 'ignorata' } | null>(null)

  const regolaScelta = regole.find((r) => r.regola === regola)
  const daSistemare = regole.reduce((s, r) => s + r.aperte + r.in_lavorazione, 0)

  async function ricontrolla() {
    setRicontrollo({ inCorso: true, esito: null }); setErroreComando(null)
    try {
      const esito = await api.post<EsitoRicontrollo>('/migrazione/ricontrolla')
      setRicontrollo({ inCorso: false, esito })
      ricarica(); ricaricaStato()
    } catch (e) {
      setErroreComando(messaggioErrore(e)); setRicontrollo({ inCorso: false, esito: null })
    }
  }

  async function segna(a: Anomalia, nuovo: string, nota?: string) {
    setInCorso(a.id); setErroreComando(null)
    try {
      await api.post(`/migrazione/anomalie/${a.id}`, { stato: nuovo, nota: nota || null })
      setDaSegnare(null)
      ricarica(); ricaricaStato()
    } catch (e) {
      setErroreComando(messaggioErrore(e))
    } finally { setInCorso(null) }
  }

  return (
    <>
      <Riquadro titolo="Segnalazioni per tipo"
                azioni={puoDecidere && (
                  <Bottone variante="secondario" inCorso={ricontrollo.inCorso} onClick={() => void ricontrolla()}>
                    Ricontrolla adesso
                  </Bottone>
                )}>
        <p className="tenue">
          {daSistemare === 0
            ? 'Nessuna segnalazione da sistemare.'
            : `${daSistemare} segnalazioni da sistemare. Ogni notte il gestionale chiude da solo quelle il cui dato è stato corretto; «Ricontrolla adesso» lo fa subito.`}
        </p>
        {ricontrollo.esito && (
          <Avviso tipo="successo">
            {ricontrollo.esito.chiuse === 0 && ricontrollo.esito.riaperte === 0
              ? 'Nessuna segnalazione da chiudere: i dati segnalati non sono ancora stati corretti.'
              : <>
                  {ricontrollo.esito.chiuse === 1 ? 'Chiusa una segnalazione' : `Chiuse ${ricontrollo.esito.chiuse} segnalazioni`}
                  {ricontrollo.esito.riaperte > 0 && `, riaperte ${ricontrollo.esito.riaperte}`}:{' '}
                  {ricontrollo.esito.per_regola
                    .map((r) => `${titoloRegola(r.titolo)} (${r.chiuse || r.riaperte})`).join(', ')}.
                </>}
          </Avviso>
        )}
        <Tabella intestazioni={['Tipo', 'Da sistemare', 'Chiuse', 'Si chiude da sola',
                   <span key="comandi" className="solo-lettore">Comandi</span>]}
                 vuoto={<Vuoto testo="La migrazione non ha lasciato segnalazioni." />}>
          {regole.map((r) => (
            <tr key={r.regola}>
              <td data-etichetta="Tipo">
                <span className="dato-testo">
                  <span className="voce-principale">{titoloRegola(r.titolo)}</span>
                  {r.spiegazione && <span className="piccolo tenue">{r.spiegazione}</span>}
                </span>
              </td>
              <td data-etichetta="Da sistemare" className="mono a-destra">{r.aperte + r.in_lavorazione}</td>
              <td data-etichetta="Chiuse" className="mono a-destra">{r.risolte + r.ignorate}</td>
              <td data-etichetta="Si chiude da sola">{r.chiusura_automatica ? 'sì' : 'no'}</td>
              <td data-etichetta="Comandi">
                <Bottone variante="secondario" dimensione="piccolo"
                         onClick={() => aggiorna({ regola: r.regola, stato: '' })}>
                  Vedi
                </Bottone>
              </td>
            </tr>
          ))}
        </Tabella>
      </Riquadro>

      <Riquadro titolo="Segnalazioni" azioni={regola || gravita || stato !== 'da_sistemare'
        ? <Bottone variante="discreto" dimensione="piccolo"
                   onClick={() => aggiorna({ regola: '', gravita: '', stato: '' })}>Azzera i filtri</Bottone>
        : undefined}>
        <div className="filtri-report">
          <Scelta etichetta="Tipo" valore={regola} cambia={(v) => aggiorna({ regola: v })} vuota="Tutti i tipi"
                  opzioni={regole.map((r) => ({ valore: r.regola, etichetta: titoloRegola(r.titolo) }))} />
          <Scelta etichetta="Gravità" valore={gravita} cambia={(v) => aggiorna({ gravita: v })} vuota="Tutte"
                  opzioni={Object.entries(GRAVITA).map(([valore, g]) => ({ valore, etichetta: g.nome }))} />
          <Scelta etichetta="Stato" valore={stato}
                  cambia={(v) => aggiorna({ stato: v === 'da_sistemare' ? '' : v })} opzioni={FILTRO_STATO} />
        </div>
        {regolaScelta && (
          <div className="migrazione-spiegazione">
            <p><strong>Che cosa significa.</strong> {regolaScelta.spiegazione ?? 'Nessuna spiegazione disponibile.'}</p>
            <p><strong>Che cosa fare.</strong> {regolaScelta.cosa_fare ?? 'Segnalarlo a Honey & Molly.'}</p>
            <p className="tenue">
              {regolaScelta.chiusura_automatica
                ? `Si chiude da sola: ${regolaScelta.chiusura_automatica.charAt(0).toLowerCase()}${regolaScelta.chiusura_automatica.slice(1)}`
                : 'Non si chiude da sola: dopo aver sistemato, segnala «Risolta».'}
            </p>
          </div>
        )}
      </Riquadro>

      {(errore || erroreComando) && <Avviso tipo="errore">{errore ?? erroreComando}</Avviso>}
      {!errore && !dati && <Attesa />}
      {dati && (
        <Riquadro denso>
          <Tabella intestazioni={['Segnalazione', 'Dettaglio', 'Stato',
                     <span key="comandi" className="solo-lettore">Comandi</span>]}
                   vuoto={<Vuoto testo={stato === 'da_sistemare' ? 'Nessuna segnalazione da sistemare con questi filtri.' : 'Nessuna segnalazione con questi filtri.'} />}>
            {dati.map((a) => {
              const vai = a.oggetto ? collegamento(a.oggetto, puo) : null
              const chiusa = a.stato === 'risolta' || a.stato === 'ignorata'
              return (
                <tr key={a.id}>
                  <td data-etichetta="Segnalazione">
                    <span className="dato-testo">
                      <span className="voce-principale">{titoloRegola(a.titolo)}</span>
                      <span><Etichetta tono={GRAVITA[a.gravita]?.tono ?? 'neutro'}>{GRAVITA[a.gravita]?.nome ?? a.gravita}</Etichetta></span>
                      <span className="piccolo tenue">dal {giorno(a.creata_il)}</span>
                    </span>
                  </td>
                  <td data-etichetta="Dettaglio">
                    <span className="dato-testo">
                      <span className={a.riservata ? 'tenue' : undefined}>{a.descrizione}</span>
                      {a.suggerimento && <span className="piccolo tenue">{a.suggerimento}</span>}
                      {a.persone.map((p) => (
                        <span key={p.id} className="piccolo">
                          <Link to={`/persone/${p.id}`}>{p.etichetta}</Link>
                          {p.numero_scheda !== null && <span className="tenue"> · scheda {p.numero_scheda}</span>}
                        </span>
                      ))}
                      {a.persone_nascoste > 0 && (
                        <span className="piccolo tenue">
                          {a.persone_nascoste === 1
                            ? 'Una scheda non rientra nei tuoi incarichi.'
                            : `${a.persone_nascoste} schede non rientrano nei tuoi incarichi.`}
                        </span>
                      )}
                      {vai && <span className="piccolo"><Link to={vai.a}>{vai.testo}</Link></span>}
                    </span>
                  </td>
                  <td data-etichetta="Stato">
                    <span className="dato-testo">
                      <span><Etichetta tono={STATO[a.stato]?.tono ?? 'neutro'}>{STATO[a.stato]?.nome ?? a.stato}</Etichetta></span>
                      {a.risolta_il && (
                        <span className="piccolo tenue">
                          {giorno(a.risolta_il)}{a.risolta_da ? `, ${a.risolta_da}` : ''}
                        </span>
                      )}
                      {a.nota && <span className="piccolo migrazione-nota">{a.nota}</span>}
                    </span>
                  </td>
                  <td data-etichetta="Comandi">
                    {puoDecidere && (
                      <div className="riga">
                        {!chiusa && (
                          <>
                            <Bottone variante="primario" dimensione="piccolo" disabled={inCorso === a.id}
                                     onClick={() => setDaSegnare({ anomalia: a, stato: 'risolta' })}>
                              Risolta
                            </Bottone>
                            <Bottone variante="secondario" dimensione="piccolo" disabled={inCorso === a.id}
                                     onClick={() => setDaSegnare({ anomalia: a, stato: 'ignorata' })}>
                              Da ignorare
                            </Bottone>
                            {a.stato === 'aperta' && (
                              <Bottone variante="discreto" dimensione="piccolo" disabled={inCorso === a.id}
                                       onClick={() => void segna(a, 'in_lavorazione')}>
                                La prendo in carico
                              </Bottone>
                            )}
                          </>
                        )}
                        {chiusa && (
                          <Bottone variante="discreto" dimensione="piccolo" disabled={inCorso === a.id}
                                   onClick={() => void segna(a, 'aperta')}>
                            Riapri
                          </Bottone>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </Tabella>
          <Paginazione salta={finestra.salta} limite={LIMITE} totale={finestra.totale}
                       ricevute={dati.length} cambia={finestra.cambia} />
        </Riquadro>
      )}

      {daSegnare && (
        <ModaleSegna anomalia={daSegnare.anomalia} stato={daSegnare.stato}
                     regola={regole.find((r) => r.regola === daSegnare.anomalia.regola)}
                     inCorso={inCorso === daSegnare.anomalia.id}
                     annulla={() => setDaSegnare(null)}
                     conferma={(nota) => void segna(daSegnare.anomalia, daSegnare.stato, nota)} />
      )}
    </>
  )
}

/** «Risolta» o «Da ignorare», con la nota: per ignorare la nota serve, perche' la segnalazione non torna. */
function ModaleSegna({ anomalia, stato, regola, inCorso, annulla, conferma }: {
  anomalia: Anomalia; stato: 'risolta' | 'ignorata'; regola: Regola | undefined; inCorso: boolean
  annulla: () => void; conferma: (nota: string) => void
}) {
  const [nota, setNota] = useState(anomalia.nota ?? '')
  const [errore, setErrore] = useState<string | null>(null)
  const ignora = stato === 'ignorata'
  const invia = () => {
    if (ignora && nota.trim().length < 3) { setErrore('Scrivi perché si può ignorare.'); return }
    conferma(nota.trim())
  }
  return (
    <Modale titolo={ignora ? 'Segnalazione da ignorare' : 'Segnalazione risolta'} chiudi={annulla}
            azioni={
              <>
                <Bottone variante="discreto" onClick={annulla}>Annulla</Bottone>
                <Bottone variante="primario" inCorso={inCorso} onClick={invia}>
                  {ignora ? 'Ignora la segnalazione' : 'Segna come risolta'}
                </Bottone>
              </>
            }>
      <p><strong>{titoloRegola(anomalia.titolo)}.</strong> {anomalia.descrizione}</p>
      {regola?.cosa_fare && <p className="tenue">{regola.cosa_fare}</p>}
      <AreaTesto etichetta={ignora ? 'Perché si può ignorare' : 'Nota (facoltativa)'} valore={nota}
                 cambia={(v) => { setNota(v); setErrore(null) }} maxLength={1000} obbligatorio={ignora}
                 errore={errore}
                 aiuto={ignora ? 'Per esempio: verificato con chi segue la persona, il dato del vecchio archivio va bene così.'
                   : 'Che cosa è stato fatto, se serve ricordarlo.'} />
    </Modale>
  )
}
